/**
 * CKEditor 5 File Archive Plugin
 * 
 * A custom plugin that allows users to select and insert images from a file archive.
 * Provides a modern, accessible UI with grid layout and thumbnail previews.
 */

import { Plugin } from 'ckeditor5';
import { ButtonView, DropdownButtonView, createDropdown, addToolbarToDropdown } from 'ckeditor5';

export default class FileArchive extends Plugin {
	static get pluginName() {
		return 'FileArchive';
	}

	init() {
		const editor = this.editor;
		
		// Register the UI component
		editor.ui.componentFactory.add('FileArchive', locale => {
			const dropdownView = createDropdown(locale);
			
			// Configure the dropdown button
			dropdownView.buttonView.set({
				label: 'Image Archive',
				icon: imageIcon,
				tooltip: true,
				withText: false
			});

			// Create the panel content
			const panelContent = this._createPanelContent(locale);
			dropdownView.panelView.children.add(panelContent);
			
			// Set panel properties and position
			dropdownView.panelView.extendTemplate({
				attributes: {
					class: ['ck-file-archive-panel']
				}
			});
			
			// Position the dropdown below the button (south)
			dropdownView.panelView.position = 's'; // south positioning - directly below the button

			// Apply custom styles directly to the panel element
			// This ensures the FileArchive dropdown has unique styling
			dropdownView.on('render', () => {
				const panelElement = dropdownView.panelView.element;
				if (panelElement) {
					panelElement.style.width = '400px';
					panelElement.style.height = '190px';
					panelElement.style.paddingLeft = '10px';
					panelElement.style.paddingRight = '10px';
					panelElement.style.paddingTop = '10px';
					panelElement.style.paddingBottom = '0';
					panelElement.style.overflowY = 'auto';
					panelElement.style.overflowX = 'auto';
				}
			});

			// Refresh content every time the dropdown is opened to get latest images
			dropdownView.on('change:isOpen', (evt, propertyName, isOpen) => {
				if (isOpen && panelContent.refresh) {
					panelContent.refresh();
				}
			});

			return dropdownView;
		});
	}

	_createPanelContent(locale) {
		const editor = this.editor;
		const panelContent = new PanelContentView(locale, editor);
		return panelContent;
	}
}

/**
 * Custom View for the file archive panel content
 */
import { View, FocusCycler, ViewCollection } from 'ckeditor5';
import { FocusTracker, KeystrokeHandler } from 'ckeditor5';

class PanelContentView extends View {
	constructor(locale, editor) {
		super(locale);

		this.editor = editor;
		this.selectedImage = null;
		this.focusTracker = new FocusTracker();
		this.keystrokes = new KeystrokeHandler();

		const bind = this.bindTemplate;

		// Set up template - we'll populate it in render()
		this.setTemplate({
			tag: 'div',
			attributes: {
				class: ['ck', 'ck-file-archive-content'],
				tabindex: '-1'
			}
		});
	}


	_getFileArchiveImages() {
		// Try to get fresh images from the custom property first (updated dynamically)
		// Fall back to config.get if not available
		const images = this.editor.config._fileArchiveImages || this.editor.config.get('fileArchiveImages') || [];
		return images;
	}

	_getImageSrc(image) {
		const host = window.location.protocol + '//' + window.location.hostname;
		let src = image.download || image.src || '';
		
		if (src && !src.startsWith('http')) {
			src = host + src;
		}
		
		return src;
	}

	_selectImage(itemElement, src) {
		
		
		// Remove previous selection from all items
		const items = this.element.querySelectorAll('.ck-file-archive-item');
		items.forEach((item, idx) => {
			
			item.classList.remove('ck-file-archive-item-selected');
			item.setAttribute('aria-selected', 'false');
			
			// Remove any existing blue line elements
			const existingBlueLine = item.querySelector('.ck-file-archive-blue-line');
			if (existingBlueLine) {
				existingBlueLine.remove();
			}
		});
		
		// Add selection to the EXACT clicked item
		if (itemElement && itemElement.classList) {
			
			
			itemElement.classList.add('ck-file-archive-item-selected');
			itemElement.setAttribute('aria-selected', 'true');
			this.selectedImage = src;
			
			// Add ONLY a thin blue line below the selected image
			const blueLine = document.createElement('div');
			blueLine.className = 'ck-file-archive-blue-line';
			blueLine.style.position = 'absolute';
			blueLine.style.bottom = '-1px'; // Slightly below the border
			blueLine.style.left = '0';
			blueLine.style.width = '100%';
			blueLine.style.height = '3px';
			blueLine.style.background = '#0c87f7';
			blueLine.style.borderRadius = '0 0 5px 5px';
			blueLine.style.zIndex = '999';
			blueLine.style.pointerEvents = 'none';
			
			itemElement.appendChild(blueLine);
			
		}
	}

	_insertImage() {
		if (!this.selectedImage) {
			// Select first image by default
			const items = this.element.querySelectorAll('.ck-file-archive-item');
			if (items.length > 0) {
				const firstSrc = items[0].getAttribute('data-src');
				this.selectedImage = firstSrc;
			}
		}

		if (this.selectedImage) {
			const editor = this.editor;
			const wrapInContainer = editor.config.get('wrapImageInContainer');

			// Insert the image using the editor's data API
			if (wrapInContainer) {
				// Insert HTML with lightbox wrapper
				const containerHtml = `<div class="imgPreviewContainer"><a class="imgPreviewLink" data-lightbox="imgPreviewLink" href="${this.selectedImage}"><img src="${this.selectedImage}" alt="Archive image" /></a></div>`;
				const viewFragment = editor.data.processor.toView(containerHtml);
				const modelFragment = editor.data.toModel(viewFragment);
				
				let insertedContent = null;
				editor.model.change(writer => {
					// insertContent returns the range where content was inserted
					const insertResult = editor.model.insertContent(modelFragment, editor.model.document.selection);
					insertedContent = insertResult;
				});

				// Ensure cursor is positioned after the inserted content
				// This prevents the cursor from jumping to the beginning in signature/salutation fields
				setTimeout(() => {
					editor.model.change(writer => {
						// Get the end position of the inserted content
						const selection = editor.model.document.selection;
						// Move cursor to the end of the selection (after inserted content)
						const endPosition = selection.getLastPosition();
						writer.setSelection(endPosition);
					});
				}, 50);
			} else {
				// Insert simple image using ImageInsert command
				// The built-in insertImage command handles cursor positioning automatically
				editor.execute('insertImage', { source: this.selectedImage });
			}

			// Close the dropdown
			this._closeDropdown();
		}
	}

	_cancel() {
		this._closeDropdown();
	}

	_closeDropdown() {
		// Find and close the dropdown by accessing the toolbar
		const toolbarItems = this.editor.ui.view.toolbar.items;
		
		for (const item of toolbarItems) {
			if (item.buttonView && item.buttonView.label === 'Image Archive' && item.isOpen) {
				item.isOpen = false;
				return;
			}
		}
	}

	render() {
		super.render();

		// Initially empty - will be built when dropdown opens
		// This allows us to get fresh data each time
		
		// Activate keystroke handler
		this.keystrokes.listenTo(this.element);
	}
	
	/**
	 * Called when the dropdown panel is shown
	 */
	refresh() {
		// Rebuild content every time the dropdown opens to get fresh data
		this.element.innerHTML = ''; // Clear existing content
		this._buildContent();
	}

	_buildContent() {
		const images = this._getFileArchiveImages();
		
		
		// Filter for supported image formats
		const supportedImages = images.filter(image => {
			const name = image.name || image.value || '';
			return /\.(jpg|jpeg|png|gif|tiff)$/i.test(name);
		});
		
		console.log('Supported images after filtering:', supportedImages);

		// If no images, show empty state message
		if (supportedImages.length === 0) {
			const noImagesDiv = document.createElement('div');
			noImagesDiv.className = 'ck-file-archive-no-images';
			noImagesDiv.setAttribute('tabindex', '-1');
			
			// Add icon
			const iconDiv = document.createElement('div');
			iconDiv.className = 'ck-file-archive-no-images-icon';
			iconDiv.innerHTML = emptyStateIcon;
			
			// Add message text with inline styles to prevent overriding
			const messageDiv = document.createElement('div');
			messageDiv.className = 'ck-file-archive-no-images-text';
			messageDiv.textContent = 'There is no available archived image for the area you chose';
			messageDiv.style.color = '#999999';
			messageDiv.style.fontSize = '12px';
			messageDiv.style.fontStyle = 'italic';
			messageDiv.style.lineHeight = '1.4';
			messageDiv.style.textAlign = 'center';
			
			noImagesDiv.appendChild(iconDiv);
			noImagesDiv.appendChild(messageDiv);
			
			this.element.appendChild(noImagesDiv);
			this.focusTracker.add(noImagesDiv);
			console.log('FileArchive: No images found, displaying message.');
			return; // Exit early, don't build grid/footer
		}

		// Create grid container
		const gridDiv = document.createElement('div');
		gridDiv.className = 'ck-file-archive-grid';

		// Show all images with horizontal scrolling (no empty slots)
		supportedImages.forEach((image, i) => {
			const src = this._getImageSrc(image);
				
				const itemDiv = document.createElement('div');
				itemDiv.className = 'ck-file-archive-item';
				itemDiv.setAttribute('data-index', i);
				itemDiv.setAttribute('data-src', src);
				itemDiv.setAttribute('role', 'button');
				itemDiv.setAttribute('tabindex', '0');
				itemDiv.setAttribute('aria-label', `Select image ${image.name || 'from archive'}`);

				const innerDiv = document.createElement('div');
				innerDiv.className = 'ck-file-archive-item-inner';

				const img = document.createElement('img');
				img.src = src;
				img.alt = image.name || 'Archive image';
				img.loading = 'lazy';

				innerDiv.appendChild(img);
				itemDiv.appendChild(innerDiv);

				// Add event listeners - store data on the element itself to avoid closure issues
				itemDiv._imageSrc = src;  // Store src directly on the DOM element
				itemDiv._imageIndex = i;  // Store index directly on the DOM element
				
				itemDiv.addEventListener('click', (e) => {
					e.preventDefault();
					e.stopPropagation();
					const clicked = e.currentTarget;
					
					this._selectImage(clicked, clicked._imageSrc);
				});

				itemDiv.addEventListener('keydown', (e) => {
					if (e.keyCode === 13 || e.keyCode === 32) { // Enter or Space
						e.preventDefault();
						e.stopPropagation();
						const clicked = e.currentTarget;
						this._selectImage(clicked, clicked._imageSrc);
					}
				});

				// Register with focus tracker
				this.focusTracker.add(itemDiv);

				gridDiv.appendChild(itemDiv);
		});
		
		

		// Create footer
		const footerDiv = document.createElement('div');
		footerDiv.className = 'ck-file-archive-footer';
		
		const footerContent = document.createElement('div');
		footerContent.className = 'ck-file-archive-footer-content';

		const infoDiv = document.createElement('div');
		infoDiv.className = 'ck-file-archive-info';
		infoDiv.innerHTML = '&nbsp;';

		const actionsDiv = document.createElement('div');
		actionsDiv.className = 'ck-file-archive-actions';

		// Insert button
		const insertBtn = document.createElement('button');
		insertBtn.className = 'ck ck-button ck-file-archive-button ck-file-archive-insert';
		insertBtn.type = 'button';
		insertBtn.innerHTML = '<span class="ck-button__label">Insert</span>';
		insertBtn.addEventListener('click', () => this._insertImage());
		this.focusTracker.add(insertBtn);

		// Cancel button
		const cancelBtn = document.createElement('button');
		cancelBtn.className = 'ck ck-button ck-file-archive-button ck-file-archive-cancel';
		cancelBtn.type = 'button';
		cancelBtn.innerHTML = '<span class="ck-button__label">Cancel</span>';
		cancelBtn.addEventListener('click', () => this._cancel());
		this.focusTracker.add(cancelBtn);

		actionsDiv.appendChild(insertBtn);
		actionsDiv.appendChild(cancelBtn);

		footerContent.appendChild(infoDiv);
		footerContent.appendChild(actionsDiv);
		footerDiv.appendChild(footerContent);

		// Append to main element
		this.element.appendChild(gridDiv);
		this.element.appendChild(footerDiv);
	}

	/**
	 * Focuses the first focusable element in the panel.
	 * This is required by CKEditor 5's dropdown panel system.
	 */
	focus() {
		// Try to focus the first image item
		const firstItem = this.element.querySelector('.ck-file-archive-item');
		if (firstItem) {
			firstItem.focus();
		} else {
			// If no items, focus the cancel button
			const cancelButton = this.element.querySelector('.ck-file-archive-cancel');
			if (cancelButton) {
				cancelButton.focus();
			}
		}
	}

	/**
	 * Clean up when the view is destroyed
	 */
	destroy() {
		super.destroy();
		
		this.focusTracker.destroy();
		this.keystrokes.destroy();
	}
}

// SVG Icon for the button
const imageIcon = `<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
	<path d="M6.91 10.54c.26-.23.64-.21.88.03l3.36 3.14 2.23-2.06a.64.64 0 0 1 .87 0l2.52 2.97V4.5H3.2v10.12l3.71-4.08zm10.27-7.51c.6 0 1.09.47 1.09 1.05v11.84c0 .59-.49 1.06-1.09 1.06H2.79c-.6 0-1.09-.47-1.09-1.06V4.08c0-.58.49-1.05 1.1-1.05h14.38zm-5.22 5.56a1.96 1.96 0 1 1 3.4-1.96 1.96 1.96 0 0 1-3.4 1.96z"/>
</svg>`;

// SVG Icon for empty state
const emptyStateIcon = `<svg viewBox="0 0 75 64" xmlns="http://www.w3.org/2000/svg" style="width: 120px; height: 120px;">
	<path fill="#CCCCCC" d="M54 8H18c-2.2 0-4 1.8-4 4v40c0 2.2 1.8 4 4 4h36c2.2 0 4-1.8 4-4V12c0-2.2-1.8-4-4-4zm0 44H18V12h36v40z"/>
	<path fill="#CCCCCC" d="M22 20h24v2H22zm0 6h24v2H22zm0 6h16v2H22z"/>
	<circle fill="#CCCCCC" cx="32" cy="40" r="6"/>
	<path fill="#CCCCCC" d="M32 34c-3.3 0-6 2.7-6 6s2.7 6 6 6 6-2.7 6-6-2.7-6-6-6zm0 10c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z"/>
</svg>`;
